
/**
 * Module dependencies.
 */

import { CollectionProps } from 'src/types/collection';
import { color, media, units } from 'src/styles/utils';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp, theme } from 'styled-tools';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import CollectionCard from 'src/components/cards/collection-card';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Filters from 'src/components/collections/collections-list/filters';
import FiltersDisclosuresProvider from 'src/context/filters-disclosures/provider';
import FiltersListProvider from 'src/context/filters-list/provider';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import React, { Fragment, ReactElement, useEffect, useMemo } from 'react';
import Type from 'src/components/core/typography';
import map from 'lodash/map';
import qs from 'qs';
import styled, { createGlobalStyle, css } from 'styled-components';
import useCollections from 'src/api/app/collections/use-collections';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';

/**
 * `PageProps` type.
 */

type PageProps = {
  descriptionSeo?: string | null,
  id: string,
  slug: string,
  subtitle: string,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * Export `Props` type.
 */

export type Props = {
  collections: {
    pageParams: number[],
    pages: Array<{
      data: CollectionProps[],
      nextPage: number | null,
      total: number
    }>
  },
  data: PageProps,
  isKiosk?: boolean,
  slug: string
};

/**
 * `GlobalStyle` component.
 */

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${color('beige100')};
  }
`;

/**
 * `Section` styled component.
 */

const Section = styled.section<{
  isKiosk?: boolean,
  isLoading: boolean
}>`
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(10)};
  position: relative;

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}

  ${ifProp('isKiosk', css`
    padding-bottom: ${units(19)};
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title    .'
      '. subTitle .'
      '. filters  .'
      '. list     .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;
  padding-top: ${units(10)};

  span {
    display: block;
  }
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H5).attrs({
  as: 'h2'
})`
  grid-area: subTitle;
  margin-bottom: ${units(6)};
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `FiltersWrapper` styled component.
 */

const FiltersWrapper = styled.div`
  grid-area: filters;
`;

/**
 * `ListWrapper` styled component.
 */

const ListWrapper = styled.section`
  grid-area: list;
  min-height: 250px;
  position: relative;
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  text-align: center;
`;

/**
 * `List` styled component.
 */

const List = styled.div`
  display: grid;
  grid-gap: ${units(4)};
  grid-template-columns: 100%;

  ${media.min('xs')`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.min('md')`
    grid-template-columns: repeat(3, 1fr);
  `}
`;

/**
 * `LoadMore` styled component.
 */

const LoadMore = styled.div`
  padding: ${units(13)} 0;
  text-align: center;
`;

/**
 * `CollectionsPage` component.
 */

const CollectionsPage = (props: Props): ReactElement => {
  const { translate } = useTranslate();
  const { asPath, query } = useRouter();
  const filterParams = useMemo(() => {
    const hasSearch = typeof window !== 'undefined' ? window.location.search : '';
    const searchFromPath = asPath.includes('?') ? `?${asPath.split('?')[1]}` : '';
    const search = hasSearch || searchFromPath;
    const parsed = search ? qs.parse(search.slice(1)) : query;

    return {
      ...parsed,
      sort: 'name'
    };
  }, [asPath, query]);

  const {
    data: pageData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const {
    data: collectionsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    refetch
  } = useCollections({
    filterParams,
    initialData: props?.collections
  });

  const isUpdating = isFetchingNextPage || isFetching;
  const total = collectionsData?.pages[0]?.total ?? 0;

  useNavbarTheme({ theme: 'dark' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [query]);

  return (
    <FiltersListProvider>
      <>
        <Metatags
          description={pageData?.descriptionSeo}
          hrefLangs={getHrefLangs(pageData?.translations, 'collections')}
          isKiosk={props?.isKiosk}
          pageTitle={pageData?.title}
          title={pageData?.titleSeo}
        />

        <GlobalStyle />

        <Section
          isKiosk={props?.isKiosk}
          isLoading={isLoadingPage}
        >
          <Loading active={isLoadingPage} />

          {isSuccessPage && (
            <Container>
              <Grid>
                <Title>
                  <FadeInUpAnimation>
                    {`${pageData?.title} `}
                  </FadeInUpAnimation>
                </Title>

                <SubTitle>
                  <FadeInUpAnimation>
                    {pageData?.subtitle}
                  </FadeInUpAnimation>
                </SubTitle>

                <FiltersWrapper>
                  <FiltersDisclosuresProvider>
                    <Filters
                      isKiosk={props?.isKiosk}
                      isUpdating={isUpdating}
                      isUpdatingSucceed={isSuccess}
                      onUpdate={refetch}
                    />
                  </FiltersDisclosuresProvider>
                </FiltersWrapper>

                {isSuccess && (
                  <ListWrapper>
                    <Loading active={isLoading || isUpdating} />

                    {!isLoading && !isUpdating && total === 0 && (
                      <Empty>
                        <Type.H6 paddingVertical={units(1)}>
                          <FadeInUpAnimation>
                            {translate('collections:list.emptyList')}
                          </FadeInUpAnimation>
                        </Type.H6>
                      </Empty>
                    )}

                    {total > 0 && (
                      <>
                        <List>
                          {map(collectionsData?.pages, (collections, index: number) => (
                            <Fragment key={index}>
                              {map(collections?.data, (collection: CollectionProps, collectionIndex: number) => (
                                <FadeInUpAnimation key={collectionIndex}>
                                  <CollectionCard
                                    isKioskCard={props?.isKiosk}
                                    key={collection.id}
                                    {...collection}
                                  />
                                </FadeInUpAnimation>
                              ))}
                            </Fragment>
                          ))}
                        </List>

                        {hasNextPage && (
                          <FadeInUpAnimation>
                            <LoadMore>
                              <Button
                                disabled={isUpdating}
                                isLoading={isUpdating}
                                onClick={() => fetchNextPage()}
                              >
                                {translate('common:actions.loadMore')}
                              </Button>
                            </LoadMore>
                          </FadeInUpAnimation>
                        )}
                      </>
                    )}
                  </ListWrapper>
                )}
              </Grid>
            </Container>
          )}
        </Section>
      </>
    </FiltersListProvider>
  );
};

/**
 * Export `CollectionsPage` component.
 */

export default CollectionsPage;
